import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import ProgressBar from 'tio-ui/components/progress-bar';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Button from 'tio-ui/components/button';
import StatusBadge from 'tio-ui/components/status-badge';
import Timeline from 'tio-ui/components/timeline';
import { eq } from 'tio-ui/utilities';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import PageHero from 'tio-ui/components/page-hero';
import {
  PhPath,
  PhTarget,
  PhCheckCircle,
  PhClock,
  PhCalendar,
  PhTrendUp,
  PhPencil,
  PhPlus,
  PhArrowRight,
  PhStar,
  PhSparkle,
  PhDownload,
  PhShare,
  PhRobot,
  PhBriefcase,
  PhGraduationCap,
  PhCertificate,
  PhFlag,
} from 'ember-phosphor-icons';
import type {
  CareerPlan,
  EmployeeCareerProfile,
  Milestone,
} from 'tio-employee/services/career-pathways-api';

interface CareerPathwaysPlanSignature {
  Args: {
    model: {
      careerPlan: CareerPlan | null;
      employee: EmployeeCareerProfile;
    };
  };
}

interface TimelineItem {
  id: string;
  kind: 'start' | 'milestone';
  milestone?: Milestone;
  title: string;
  date?: string;
}

const STATUS_CONFIG = {
  completed: { color: 'green', icon: PhCheckCircle, label: 'Completed' },
  'in-progress': { color: 'violet', icon: PhClock, label: 'In Progress' },
  planned: { color: 'gray', icon: PhCalendar, label: 'Planned' },
};

const TYPE_ICONS = {
  certification: PhCertificate,
  course: PhGraduationCap,
  experience: PhBriefcase,
  milestone: PhFlag,
};

export default class CareerPathwaysPlanIndex extends Component<CareerPathwaysPlanSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @tracked editingMilestoneId: string | null = null;

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get dataSource(): DataSource {
    // Career plan data is entirely demo — no backend API yet
    return this.isDemoMode ? 'demo' : 'placeholder';
  }

  get careerPlan() {
    return this.args.model.careerPlan;
  }

  get employee() {
    return this.args.model.employee;
  }

  get milestones() {
    return this.careerPlan?.milestones ?? [];
  }

  get timelineItems(): TimelineItem[] {
    const items: TimelineItem[] = [
      {
        id: 'start',
        kind: 'start',
        title: this.employee.currentRole.title,
        date: this.employee.currentRole.startDate,
      },
    ];

    this.milestones.forEach((milestone) => {
      items.push({
        id: milestone.id,
        kind: 'milestone',
        milestone,
        title: milestone.title,
        date: milestone.completedDate ?? milestone.targetDate,
      });
    });

    return items;
  }

  get goalTitle() {
    return this.careerPlan?.targetRoleTitle || 'Your Goal';
  }

  get goalProgress() {
    return this.careerPlan?.progress ?? 0;
  }

  get estimatedTimeRemaining() {
    return this.careerPlan?.estimatedTimeMonths
      ? `${this.careerPlan.estimatedTimeMonths} months`
      : 'TBD';
  }

  get targetDate() {
    return this.careerPlan?.targetDate;
  }

  get aiSuggestions() {
    if (this.isDemoMode) {
      const clientId = this.demoClient.client.id;

      if (clientId === 'ups') {
        return [
          {
            title: 'Speed up your timeline:',
            body: 'The Supervisor Leadership Program starts next month and accelerates promotion readiness.',
          },
          {
            title: 'Internal opportunity:',
            body: 'Volunteer to lead a peak season shift to build leadership experience.',
          },
        ];
      }

      if (clientId === 'starbucks') {
        return [
          {
            title: 'Speed up your timeline:',
            body: 'The Store Manager Development cohort starts next month and compresses training by 6 weeks.',
          },
          {
            title: 'Internal opportunity:',
            body: 'Shadow a high-volume store manager to build scheduling and coaching experience.',
          },
        ];
      }

      return [
        {
          title: 'Speed up your timeline:',
          body: 'The Charge Nurse Training cohort starts next month - it’s more intensive but 40% faster than self-paced.',
        },
        {
          title: 'Internal opportunity:',
          body: 'A unit manager is looking for a nurse to shadow a Nurse Manager. Great way to build leadership experience!',
        },
      ];
    }

    const inProgress = this.milestones.find((milestone) => milestone.status === 'in-progress');
    const goal = this.careerPlan?.targetRoleTitle || 'your target role';

    return [
      {
        title: 'Next best step:',
        body: inProgress
          ? `Stay on track with "${inProgress.title}" to keep momentum toward ${goal}.`
          : `Set a milestone to move closer to ${goal}.`,
      },
      {
        title: 'Internal opportunity:',
        body: 'Look for stretch assignments that align with your current role and target path.',
      },
    ];
  }

  formatDate = (dateValue?: string) => {
    if (!dateValue) return 'TBD';
    const date = new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  editMilestone = (id: string) => {
    this.editingMilestoneId = this.editingMilestoneId === id ? null : id;
  };

  getStatusConfig = (status: string) => {
    return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.planned;
  };

  getTypeIcon = (type: string) => {
    return TYPE_ICONS[type as keyof typeof TYPE_ICONS] || PhTarget;
  };

  /**
   * Type-cast helper for Timeline items - Timeline yields unknown type,
   * this lets us safely access TimelineItem properties in templates
   */
  asTimelineItem = (item: unknown) => item as TimelineItem;

  <template>
    {{! template-lint-disable no-bare-strings }}
    <DataIndicator @source={{this.dataSource}} @showBadge={{true}} @dataSourceId="career-plan">
      <VStack class="data-audit w-full min-w-0">
        {{! Plan Header }}
        <PageHero @gradient="cool">
          <:default>
            <div class="tio-row-start-between">
              <div>
                <div class="tio-row mb-2">
                  <PhPath @size={{32}} />
                  <Heading @level={{1}} class="tio-fg-inverse">My Career Plan</Heading>
                </div>
                <Text @size="lg" class="text-teal-100 mb-4">
                  Your personalized roadmap to
                  <strong>{{this.goalTitle}}</strong>
                </Text>

                <div class="flex gap-6 text-sm">
                  <div class="tio-row-tight">
                    <PhBriefcase @size={{18}} />
                    <span>Current: {{this.employee.currentRole.title}}</span>
                  </div>
                  <div class="tio-row-tight">
                    <PhTarget @size={{18}} />
                    <span>Goal: {{this.goalTitle}}</span>
                  </div>
                  <div class="tio-row-tight">
                    <PhCalendar @size={{18}} />
                    <span>Target: {{this.formatDate this.targetDate}}</span>
                  </div>
                </div>
              </div>

              <div class="text-right">
                <div class="text-6xl font-bold">{{this.goalProgress}}%</div>
                <Text class="text-teal-100">Plan Progress</Text>
                <div class="tio-flex-tight mt-3">
                  <LinkTo @route="authenticated.reimagined.plan.roadmap">
                    <Button
                      @intent="primary"
                      @appearance="outlined"
                      @icon={{PhPath}}
                      @label="View Roadmap"
                    />
                  </LinkTo>
                  <Button
                    @intent="default"
                    @appearance="outlined"
                    @icon={{PhPencil}}
                    @label="Edit Plan"
                  />
                  <Button
                    @intent="default"
                    @appearance="outlined"
                    @icon={{PhShare}}
                    @label="Share"
                  />
                </div>
              </div>
            </div>

            {{! Progress Bar }}
            <div class="mt-6">
              <div class="tio-flex-between text-sm mb-2">
                <span>Progress</span>
                <span>{{this.estimatedTimeRemaining}} remaining</span>
              </div>
              <ProgressBar
                @value={{this.goalProgress}}
                @color="teal"
                class="[&_[data-test-progress-bar-fill]]:tio-bg-surface [&]:bg-teal-400/30"
              />
            </div>
          </:default>
        </PageHero>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {{! Main Timeline }}
          <div class="lg:col-span-2 space-y-4">
            {{! Timeline Section }}
            <Section class="tio-bg-surface rounded-lg shadow-sm">
              <:header>
                <div class="tio-row-between">
                  <div class="tio-row-tight">
                    <PhPath @size={{20}} class="tio-fg-info" />
                    <span>Your Journey</span>
                  </div>
                  <Button @intent="primary" @size="sm" @icon={{PhPlus}} @label="Add Milestone" />
                </div>
              </:header>
              <:body>
                <Timeline @items={{this.timelineItems}} @lineClass="tio-gradient-brand-soft">
                  <:node as |rawItem|>
                    {{#let (this.asTimelineItem rawItem) as |item|}}
                      {{#if (eq item.kind "start")}}
                        <div
                          class="w-6 h-6 rounded-full tio-bg-neutral ring-4 ring-white shadow"
                        ></div>
                      {{else if item.milestone}}
                        <div
                          class="w-6 h-6 rounded-full ring-4 ring-white shadow
                            {{if
                              (eq item.milestone.status 'completed')
                              'tio-bg-success'
                              (if
                                (eq item.milestone.status 'in-progress')
                                'tio-bg-brand-accent animate-pulse'
                                'bg-gray-300'
                              )
                            }}"
                        ></div>
                      {{/if}}
                    {{/let}}
                  </:node>
                  <:content as |rawItem|>
                    {{#let (this.asTimelineItem rawItem) as |item|}}
                      {{#if (eq item.kind "start")}}
                        <div class="flex-1 p-4 tio-bg-sunken rounded-lg border tio-border-default">
                          <div class="tio-row-between">
                            <div>
                              <Text @variant="muted" @size="sm">Started</Text>
                              <div class="font-semibold tio-fg-primary">
                                {{item.title}}
                              </div>
                            </div>
                            <Text @variant="muted" @size="sm">
                              {{this.formatDate item.date}}
                            </Text>
                          </div>
                        </div>
                      {{else if item.milestone}}
                        {{#let (this.getStatusConfig item.milestone.status) as |config|}}
                          {{#let (this.getTypeIcon item.milestone.type) as |TypeIcon|}}
                            <div
                              class="flex-1 p-4 rounded-lg border transition-all cursor-pointer hover:shadow-md
                                {{if
                                  (eq item.milestone.status 'completed')
                                  'tio-bg-success-soft tio-border-success'
                                  (if
                                    (eq item.milestone.status 'in-progress')
                                    'tio-bg-brand-wash tio-border-brand-soft'
                                    'tio-bg-surface tio-border-default'
                                  )
                                }}"
                            >
                              <div class="tio-row-start-between mb-2">
                                <div class="tio-row">
                                  <div
                                    class="p-2 rounded-lg
                                      {{if
                                        (eq item.milestone.status 'completed')
                                        'tio-bg-success-soft tio-fg-success'
                                        (if
                                          (eq item.milestone.status 'in-progress')
                                          'tio-bg-brand-soft tio-fg-brand'
                                          'tio-bg-sunken tio-fg-muted'
                                        )
                                      }}"
                                  >
                                    {{! @glint-ignore - dynamic icon }}
                                    <TypeIcon @size={{20}} />
                                  </div>
                                  <div>
                                    <div
                                      class="font-semibold tio-fg-primary"
                                    >{{item.milestone.title}}</div>
                                    <Text
                                      @variant="muted"
                                      @size="sm"
                                    >{{item.milestone.description}}</Text>
                                  </div>
                                </div>
                                <div class="tio-row-tight">
                                  <StatusBadge
                                    @status={{item.milestone.status}}
                                    @label={{config.label}}
                                  />
                                  <Button @intent="default" @appearance="outlined" @size="sm">
                                    <PhPencil @size={{14}} />
                                  </Button>
                                </div>
                              </div>

                              {{! Progress bar for in-progress items }}
                              {{#if (eq item.milestone.status "in-progress")}}
                                <div class="mt-3">
                                  <div class="tio-flex-between text-sm mb-1">
                                    <span>Progress</span>
                                    <span
                                      class="tio-fg-brand font-medium"
                                    >{{item.milestone.progress}}%</span>
                                  </div>
                                  <ProgressBar @value={{item.milestone.progress}} @color="blue" />
                                </div>
                              {{/if}}

                              <div class="tio-row-between mt-3 pt-3 border-t tio-border-subtle">
                                <Text @variant="muted" @size="sm">
                                  {{#if (eq item.milestone.status "completed")}}
                                    Completed:
                                    {{this.formatDate item.milestone.completedDate}}
                                  {{else}}
                                    Target:
                                    {{this.formatDate item.milestone.targetDate}}
                                  {{/if}}
                                </Text>
                                {{#if (eq item.milestone.status "planned")}}
                                  <Button
                                    @intent="primary"
                                    @size="sm"
                                    @icon={{PhArrowRight}}
                                    @iconPlacement="end"
                                    @label="Start"
                                  />
                                {{/if}}
                              </div>
                            </div>
                          {{/let}}
                        {{/let}}
                      {{/if}}
                    {{/let}}
                  </:content>
                </Timeline>

                {{! Goal }}
                <div class="relative tio-row-top-loose pl-12 mt-6">
                  <div
                    class="absolute left-3 w-7 h-7 rounded-full tio-gradient-warning border-4 border-white shadow tio-row-center"
                  >
                    <PhStar @size={{14}} class="tio-fg-inverse" />
                  </div>
                  <div
                    class="flex-1 p-4 tio-gradient-warning-soft rounded-lg border tio-border-warning"
                  >
                    <div class="tio-row-between">
                      <div>
                        <Text @variant="muted" @size="sm">Your Goal</Text>
                        <div class="tio-text-xl-bold tio-fg-warning-h">
                          {{this.goalTitle}}
                        </div>
                        <Text @variant="muted" @size="sm">
                          Target:
                          {{this.formatDate this.targetDate}}
                        </Text>
                      </div>
                      <div class="text-center p-4 bg-white/50 rounded-lg">
                        <div class="tio-text-2xl-bold tio-fg-warning">
                          {{this.estimatedTimeRemaining}}
                        </div>
                        <Text @variant="muted" @size="xs">Remaining</Text>
                      </div>
                    </div>
                  </div>
                </div>
              </:body>
            </Section>
          </div>

          {{! Sidebar }}
          <div class="space-y-6">
            {{! AI Assistant }}
            <Section
              class="tio-gradient-brand-soft rounded-lg shadow-sm border tio-border-brand-soft"
            >
              <:header>
                <div class="tio-row-tight">
                  <PhRobot @size={{20}} class="tio-fg-brand" />
                  <span>AI Suggestions</span>
                </div>
              </:header>
              <:body>
                <div class="space-y-3">
                  {{#each this.aiSuggestions as |suggestion|}}
                    <div class="p-3 tio-bg-surface rounded-lg border tio-border-brand-soft">
                      <div class="tio-row-top-tight">
                        <PhSparkle @size={{16}} class="tio-fg-brand-accent mt-0.5" />
                        <div>
                          <Text @size="sm">
                            <strong>{{suggestion.title}}</strong>
                            {{suggestion.body}}
                          </Text>
                        </div>
                      </div>
                    </div>
                  {{/each}}
                </div>
                <LinkTo @route="authenticated.reimagined.advisor">
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    class="w-full mt-4"
                    @icon={{PhRobot}}
                    @label="Chat with AI Advisor"
                  />
                </LinkTo>
              </:body>
            </Section>

            {{! Plan Stats }}
            <Section class="tio-bg-surface rounded-lg shadow-sm">
              <:header>
                <div class="tio-row-tight">
                  <PhTrendUp @size={{20}} class="tio-fg-info" />
                  <span>Plan Stats</span>
                </div>
              </:header>
              <:body>
                <div class="space-y-4">
                  <div class="tio-row-between">
                    <Text @variant="muted">Milestones</Text>
                    <span class="font-semibold">1/6 completed</span>
                  </div>
                  <div class="tio-row-between">
                    <Text @variant="muted">Skills gained</Text>
                    <span class="font-semibold">3 of 8</span>
                  </div>
                  <div class="tio-row-between">
                    <Text @variant="muted">TA used</Text>
                    <span class="font-semibold tio-fg-success">$2,340</span>
                  </div>
                  <div class="tio-row-between">
                    <Text @variant="muted">Time invested</Text>
                    <span class="font-semibold">120+ hours</span>
                  </div>
                </div>
              </:body>
            </Section>

            {{! Quick Actions }}
            <Section class="tio-bg-surface rounded-lg shadow-sm">
              <:header>Actions</:header>
              <:body>
                <div class="space-y-2">
                  <Button
                    @intent="primary"
                    class="w-full justify-start"
                    @icon={{PhPlus}}
                    @label="Add Milestone"
                  />
                  <Button
                    @intent="default"
                    @appearance="outlined"
                    class="w-full justify-start"
                    @icon={{PhCalendar}}
                    @label="Set Reminders"
                  />
                  <Button
                    @intent="default"
                    @appearance="outlined"
                    class="w-full justify-start"
                    @icon={{PhDownload}}
                    @label="Export Plan"
                  />
                  <Button
                    @intent="default"
                    @appearance="outlined"
                    class="w-full justify-start"
                    @icon={{PhShare}}
                    @label="Share with Manager"
                  />
                </div>
              </:body>
            </Section>
          </div>
        </div>
      </VStack>
    </DataIndicator>
  </template>
}
